'use client';

import { API_ROUTES } from '@shared/api-utils';
import { FormControl, FormField, FormItem } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { EditData } from '@shared/icons/system';
import { formatErrorMessages } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import { useMultistepForm } from '../../../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { PhoneNumberInput } from '../../../../../../form-controls/PhoneNumberInput/PhoneNumberInput';
import { isSamePhoneForVerificationChange, phoneNumberForVerificationFromInput } from '../../../../../../form-controls/PhoneNumberInput/phoneNumberUtils';
import { ContinueButton } from '../../../../../../Forms/Registration/components/ContinueButton/ContinueButton';
import { toast } from '../../../../../../Toast/Toast';
import { useVerificationApiMutation } from '../../../../hooks/useVerificationApiMutation';
import { isVerificationRateLimitError } from '../../../../utils/verificationErrorUtils';
import type { EditMobileNumberFormData, SendVerificationPayload, SendVerificationResponse, StepProps } from '../../types/edit-mobile-number';

type EditMobileNumberStepProps = StepProps & {
  originalPhoneVerificationValue?: string;
};

const EditMobileNumberStep = ({ namespace, originalPhoneVerificationValue }: EditMobileNumberStepProps) => {
  const { control, formState, trigger, getValues, setValue, watch } = useFormContext<EditMobileNumberFormData>();
  const { updateFormData, goToNextStep } = useMultistepForm();
  const t = useTranslations(namespace);
  const tOtp = useTranslations('form.otp');
  const phoneConfig = getSiteConfig().PHONE_NUMBER;
  const FORM_ID = 'edit-mobile-number-form';
  const { errors } = formState;
  const mobileNumberErrorMessages = formatErrorMessages([errors.mobileNumber?.prefix?.message, errors.mobileNumber?.number?.message]);

  const { mutateAsync: sendVerification, isPending } = useVerificationApiMutation<SendVerificationResponse, SendVerificationPayload>(API_ROUTES.MY_ACCOUNT.VERIFICATION.SEND);

  const watchedMobileNumber = watch('mobileNumber');
  const prefix = watchedMobileNumber?.prefix ?? '';
  const number = watchedMobileNumber?.number ?? '';
  const isMobileNumberChanged = originalPhoneVerificationValue
    ? !isSamePhoneForVerificationChange(prefix, number, originalPhoneVerificationValue)
    : number.replace(/\D/g, '').length > 0;
  const isContinueDisabled = !isMobileNumberChanged || isPending;
  const phoneInputHelpMessageMode = 'focusout' as const;

  const validateMobileNumber = async () => trigger('mobileNumber');

  const handleContinue = async () => {
    if (!isMobileNumberChanged) {
      return;
    }

    const isValid = await validateMobileNumber();
    if (!isValid) return;

    const { prefix: currentPrefix, number: currentNumber } = getValues('mobileNumber');
    const phoneValue = phoneNumberForVerificationFromInput(currentPrefix, currentNumber);

    try {
      const response = await sendVerification({ method: 'phone', value: phoneValue });

      if (response?.verificationId) {
        setValue('verificationId', response.verificationId);
        updateFormData('verificationId', response.verificationId);
        updateFormData('mobileNumber', getValues('mobileNumber'));
        goToNextStep();
        return;
      }

      toast(t('error'), { type: 'error' });
    } catch (error) {
      if (isVerificationRateLimitError(error)) {
        toast(tOtp('maxAttemptsReached'), { type: 'error' });
        return;
      }
      toast(t('error'), { type: 'error' });
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="edit-mobile-number-step">
      <div className="md:my-6">
        <EditData size="extra-extra-large" className="mb-5" data-testid="edit-data-icon" />
        <h4 className="mb-2">{t('title')}</h4>
        <p className="text-foreground-subtle mb-8">{t('description')}</p>

        <div className="flex flex-col gap-4">
          <FormField
            control={control}
            name="mobileNumber"
            render={({ field, fieldState }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <PhoneNumberInput
                    id={`${FORM_ID}-mobileNumber`}
                    label={t('mobileNumber')}
                    placeholder={t('placeholder')}
                    useDynamicPlaceholder={false}
                    formatNationalInput={phoneConfig.formatNationalInput}
                    hidePrefix={!phoneConfig.showCountryPrefix}
                    helpMessageMode={phoneInputHelpMessageMode}
                    helpMessageType={fieldState.error ? 'error' : undefined}
                    helpMessage={mobileNumberErrorMessages}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    prefixTrackingProps={{
                      'data-track-name': 'Country Code',
                      'data-track-privacy': 'Mask',
                    }}
                    inputTrackingProps={{
                      'data-track-name': 'Mobile',
                      'data-track-privacy': 'Mask',
                    }}
                    helperTrackingProps={{
                      'data-track-helper': 'Mobile',
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      <div className="text-center mt-auto md:mt-8" data-track-container="Actions">
        <ContinueButton onClick={handleContinue} disabled={isContinueDisabled} data-track-name="Continue" loading={isPending} />
      </div>
    </div>
  );
};

export { EditMobileNumberStep };
